import { useState } from 'react';
import { useShell } from '../../app/ShellContext.tsx';
import type { RouteResult } from '../api-client.ts';
import type { ApprovalGateKind, MetricCard } from '../projections.ts';

interface MetricActionPorts {
  readonly onExercise: (kind: ApprovalGateKind, objectId: string) => Promise<RouteResult>;
  readonly onDelete: (metricId: string) => Promise<RouteResult>;
  /** Route a collect-now intent for an ACTIVE Metric; the parent re-reads on ok (G-182). */
  readonly onCollectNow: (metricId: string) => Promise<RouteResult>;
}

/** Transient action state for Metric detail; durable state is always re-read by the parent surface. */
export function useMetricDetailActions(metric: MetricCard, ports: MetricActionPorts) {
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const shell = useShell();

  // One busy-guarded runner for every routed write; a refusal surfaces as the transient note.
  const run = (action: () => Promise<RouteResult>, failNote: string): void => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    void action()
      .then((result) => {
        if (!result.ok) setNote(result.note ?? failNote);
      })
      .finally(() => setBusy(false));
  };
  const finishSetup = (): void => {
    const questions = metric.clarificationQuestions.join(' ');
    const clarification =
      questions.length > 0 ? ` The recorded clarification questions are: ${questions}` : '';
    shell.openNewChatWithDraft(
      `Finish setting up the proposed KPI “${metric.name}”. Resolve any definition clarification with me, then register and trial its collector so it is ready for activation.${clarification}`,
    );
  };
  return {
    note,
    busy,
    confirmDelete,
    route: (kind: ApprovalGateKind, objectId: string): void =>
      run(() => ports.onExercise(kind, objectId), 'That action was not permitted.'),
    finishSetup,
    startTask: (): void =>
      shell.openNewChatWithDraft(
        `Draft a task aimed at the KPI “${metric.name}”. What I want to move, and by when: `,
      ),
    deleteProposal: (): void =>
      run(() => ports.onDelete(metric.id), 'The metric could not be deleted.'),
    collectNow: (): void =>
      run(() => ports.onCollectNow(metric.id), 'That collection could not be run.'),
    beginDelete: () => setConfirmDelete(true),
    cancelDelete: () => setConfirmDelete(false),
  };
}
